import { Injectable } from '@angular/core';
import { GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import { GridInput } from '@it2go/data-grid';
import { UserTaskGridObject, UserTaskObject } from '@libs/types/src/services/object-type/user/user-task.object';
import { filterFrag } from './types/filter.types';
import { IdInput } from '@libs/types/src/common/input-type/id.input';
import { OkObject } from '@it2go/types';
import { UserTaskCreateInput } from '@libs/types/src/services/input-type/user/user-task-create.input';
import { UserTaskUpdateInput } from '@libs/types/src/services/input-type/user/user-task-update.input';
import { UserTaskTagsInput } from '@libs/types/src/services/input-type/user/user-task-tags.input';
import {
    UserTaskEntryGridObject,
    UserTaskEntryObject,
} from '@libs/types/src/services/object-type/user/user-task-entry.object';
import { UserTaskEntryCreateInput } from '@libs/types/src/services/input-type/user/user-task-entry-create.input';
import { UserTaskEntryUpdateInput } from '@libs/types/src/services/input-type/user/user-task-entry-update.input';
import {
    UserTaskAppointmentGridObject,
    UserTaskAppointmentObject,
} from '@libs/types/src/services/object-type/user/user-task-appointment.object';
import {
    UserTaskAppointmentCreateInput,
} from '@libs/types/src/services/input-type/user/user-task-appointment-create.input';
import {
    UserTaskAppointmentUpdateInput,
} from '@libs/types/src/services/input-type/user/user-task-appointment-update.input';

@Injectable({
    providedIn: 'root',
})
export class UserTaskGqlService extends GqlServiceV2 {

    public async getUserTasks(input: GridInput, fragment: string): Promise<UserTaskGridObject> {
        return this.query<UserTaskGridObject>(
            `
                query($input: GridInput!) {
                    userTask {
                        filter (input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getUserTaskUserTags(input: UserTaskTagsInput): Promise<string[]> {
        return this.query<string[]>(
            `
                query($input: UserTaskTagsInput!) {
                    userTaskTag {
                        userTags (input: $input)
                    }
                }
            `,
            { input },
        );
    }

    public async createUserTask(input: UserTaskCreateInput, fragment: string): Promise<UserTaskObject> {
        return this.mutate<UserTaskObject>(
            `
                mutation($input: UserTaskCreateInput!) {
                    userTask {
                        create (input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async updateUserTask(input: UserTaskUpdateInput, fragment: string): Promise<UserTaskObject> {
        return this.mutate<UserTaskObject>(
            `
                mutation($input: UserTaskUpdateInput!) {
                    userTask {
                        update (input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async deleteUserTask(input: IdInput, fragment: string = 'ok'): Promise<OkObject> {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    userTask {
                        delete (input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getUserTaskEntries(input: GridInput, fragment: string): Promise<UserTaskEntryGridObject> {
        return this.query<UserTaskEntryGridObject>(
            `
                query($input: GridInput!) {
                    userTaskEntry {
                        filter (input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async createUserTaskEntry(input: UserTaskEntryCreateInput, fragment: string): Promise<UserTaskEntryObject> {
        return this.mutate<UserTaskEntryObject>(
            `
                mutation($input: UserTaskEntryCreateInput!) {
                    userTaskEntry {
                        create (input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async updateUserTaskEntry(input: UserTaskEntryUpdateInput, fragment: string): Promise<UserTaskEntryObject> {
        return this.mutate<UserTaskEntryObject>(
            `
                mutation($input: UserTaskEntryUpdateInput!) {
                    userTaskEntry {
                        update (input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async deleteUserTaskEntry(input: IdInput, fragment: string = 'ok'): Promise<OkObject> {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    userTaskEntry {
                        delete (input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getUserTaskAppointments(input: GridInput, fragment: string): Promise<UserTaskAppointmentGridObject> {
        return this.query<UserTaskAppointmentGridObject>(
            `
                query($input: GridInput!) {
                    userTaskAppointment {
                        filter (input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async createUserTaskAppointment(input: UserTaskAppointmentCreateInput, fragment: string): Promise<UserTaskAppointmentObject> {
        return this.mutate<UserTaskAppointmentObject>(
            `
                mutation($input: UserTaskAppointmentCreateInput!) {
                    userTaskAppointment {
                        create (input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async updateUserTaskAppointment(input: UserTaskAppointmentUpdateInput, fragment: string): Promise<UserTaskAppointmentObject> {
        return this.mutate<UserTaskAppointmentObject>(
            `
                mutation($input: UserTaskAppointmentUpdateInput!) {
                    userTaskAppointment {
                        update (input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async deleteUserTaskAppointment(input: IdInput, fragment: string = 'ok'): Promise<OkObject> {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    userTaskAppointment {
                        delete (input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }
}
